import { SaleStateSchema, type SaleState } from '@neon-one/auctions-types';

import { lotLabel } from './format';
import { ReassignAddress, type ReassignSubject } from './reassign-target';
import type { LotDetail } from './types';

export const ReassignMode = {
  SendOffer: 'offer',
  ReassignNow: 'now',
} as const;
export type ReassignModeValue = (typeof ReassignMode)[keyof typeof ReassignMode];

export const ItemAction = {
  ViewBidder: 'view_bidder',
  ReassignNow: 'reassign_now',
  SendOffer: 'send_offer',
  SellToBidder: 'sell_to_bidder',
  OfferToBidder: 'offer_to_bidder',
  Withdraw: 'withdraw',
} as const;
export type ItemActionValue = (typeof ItemAction)[keyof typeof ItemAction];

export interface ItemActionSpec {
  action: ItemActionValue;
  label: string;
  icon: string;
  // Null on the action that opens a drawer rather than the placement dialog.
  mode: ReassignModeValue | null;
  // The two placement actions are what the move-to-next-bidder flag gates.
  flagged: boolean;
}

/** What the menu needs to know about the lot under the cursor. */
export interface ItemActionContext {
  /**
   * Somebody is answerable for this lot right now: a holder whose payment has
   * not been refunded away, or the target of a standing offer. Not "there is a
   * name in the winner column" — a released lot still names who used to have it.
   */
  hasAssignee: boolean;
  saleCancelled: boolean;
  saleState?: SaleState;
  sellToBidder?: boolean;
  /** The auction is archived: its bidder site, where offers are accepted, is gone. */
  archived?: boolean;
}

/** The menu's actions for a row, after the move-to-next-bidder flag is applied. */
export function menuActionsFor(ctx: ItemActionContext, moveToNextBidder: boolean): ItemActionSpec[] {
    return itemActionsFor(ctx).filter((spec) => !spec.flagged || moveToNextBidder);
}

const VIEW_BIDDER: ItemActionSpec = {
  action: ItemAction.ViewBidder,
  label: 'View bidder',
  icon: 'pi pi-user',
  mode: null,
  flagged: false,
};

const REASSIGN_NOW: ItemActionSpec = {
  action: ItemAction.ReassignNow,
  label: 'Reassign now',
  icon: 'pi pi-arrow-right-arrow-left',
  mode: ReassignMode.ReassignNow,
  flagged: true,
};

const SEND_OFFER: ItemActionSpec = {
  action: ItemAction.SendOffer,
  label: 'Send offer',
  icon: 'pi pi-send',
  mode: ReassignMode.SendOffer,
  flagged: true,
};

const SELL_TO_BIDDER: ItemActionSpec = {
    action: ItemAction.SellToBidder,
    label: 'Sell to bidder',
    icon: 'pi pi-check-circle',
    mode: ReassignMode.ReassignNow,
    flagged: false,
};
const OFFER_TO_BIDDER: ItemActionSpec = {
    action: ItemAction.OfferToBidder,
    label: 'Offer to bidder',
    icon: 'pi pi-send',
    mode: ReassignMode.SendOffer,
    flagged: false,
};
// Takes the lot out of every sale for good. Drawer-only for a lot someone holds,
// where it has to weigh payment state first.
const WITHDRAW: ItemActionSpec = {
    action: ItemAction.Withdraw,
    label: 'Withdraw item',
    icon: 'pi pi-ban',
    mode: null,
    flagged: false,
};
const SALE = SaleStateSchema.enum;
const UNSOLD_STATES: ReadonlySet<SaleState> = new Set([SALE.unsold, SALE.needs_buyer, SALE.offer_ended]);

export function isUnsoldLot(saleState: SaleState | undefined): boolean {
    return saleState !== undefined && UNSOLD_STATES.has(saleState);
}

export function itemActionsFor(ctx: ItemActionContext): readonly ItemActionSpec[] {
    const actions = placementActionsFor(ctx);
    return ctx.archived ? actions.filter((spec) => spec.mode !== ReassignMode.SendOffer) : actions;
}

function placementActionsFor(ctx: ItemActionContext): ItemActionSpec[] {
    const actions: ItemActionSpec[] = ctx.hasAssignee ? [VIEW_BIDDER] : [];
    if (ctx.saleCancelled || ctx.saleState === SALE.sale_canceled) return actions;
    if (ctx.sellToBidder && isUnsoldLot(ctx.saleState) && !ctx.hasAssignee) {
        return [...actions, SELL_TO_BIDDER, OFFER_TO_BIDDER, WITHDRAW];
    }
    // An offer on a lot nobody holds: placing it again would supersede the
    // offer, so the menu keeps to the one action that does not.
    if (ctx.sellToBidder && ctx.saleState === SALE.offer_outstanding && !ctx.hasAssignee) {
        return [...actions, WITHDRAW];
    }
    if (ctx.saleState !== SALE.invoicing) actions.push(REASSIGN_NOW, SEND_OFFER);
    return actions;
}

export function reassignSubjectFor(detail: LotDetail, sellToBidder: boolean): ReassignSubject | null {
    const context = {
        lotRef: detail.lotRef, lot: lotLabel(detail.lotNumber), itemTitle: detail.itemTitle,
        amountCents: detail.amountCents, fmvCents: detail.fmvCents, saleState: detail.saleState,
    };
    if (sellToBidder && isUnsoldLot(detail.saleState)) {
        return { ...context, address: ReassignAddress.Lot, lineId: null, invoiceUuid: null,
            holderName: null, invoiceNumber: null, status: null, refundedCents: 0 };
    }
    const { line, invoice } = detail;
    if (!line || !invoice) return null;
    const held = !line.declined;
    return { ...context, lot: lotLabel(line.lotNumber), itemTitle: line.itemTitle,
        amountCents: line.amountCents, fmvCents: line.fmvCents,
        lineId: line.lineId, invoiceUuid: invoice.invoiceUuid,
        holderName: held ? invoice.bidderName : null, invoiceNumber: held ? invoice.invoiceNumber : null,
        status: invoice.status, refundedCents: line.refundedCents };
}
